import { useState, useEffect, useCallback } from "react";
import { Search, Filter, ShoppingCart, RefreshCw, X, Calendar, Truck, Factory, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SalesOrders = () => {
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [plantFilter, setPlantFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [activeConnection, setActiveConnection] = useState(null);
  const [dataSource, setDataSource] = useState("loading");
  
  // Fetch active connection
  useEffect(() => {
    const fetchConnection = async () => {
      try {
        const response = await axios.get(`${API}/sap-connections`);
        const connections = response.data.filter(c => c.is_active);
        if (connections.length > 0) {
          setActiveConnection(connections[0]);
        }
      } catch (error) {
        console.error("Failed to fetch connections:", error);
      }
    };
    fetchConnection();
  }, []);
  
  const fetchSalesOrders = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (plantFilter !== "all") params.append("plant", plantFilter);
      if (activeConnection) params.append("connection_id", activeConnection.id);
      
      const response = await axios.get(`${API}/sales-orders?${params.toString()}`);
      setSalesOrders(response.data);
      
      setDataSource("sap_live");
    } catch (error) {
      console.error("Failed to fetch sales orders:", error);
      setSalesOrders([]);
      const detail = error.response?.data?.detail;
      setDataSource(detail?.code || "SAP_RESPONSE_ERROR");
      toast.error(detail?.message || "Failed to load sales orders");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [searchQuery, statusFilter, plantFilter, activeConnection]);
  
  useEffect(() => {
    fetchSalesOrders();
  }, [fetchSalesOrders]);
  
  useEffect(() => {
    if (activeConnection) {
      setLoading(true);
      fetchSalesOrders();
    }
  }, [activeConnection]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const handleRefresh = () => {
    setRefreshing(true);
    fetchSalesOrders();
    toast.success("Sales orders refreshed");
  };
  
  const handleClearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setPlantFilter("all");
  };
  
  const hasActiveFilters = searchQuery || statusFilter !== "all" || plantFilter !== "all";
  
  // Calculate stats
  const orderCurrencies = [...new Set(salesOrders.map((so) => so.currency).filter(Boolean))];
  const stats = {
    totalOrders: [...new Set(salesOrders.map(so => so.sales_order))].length,
    totalItems: salesOrders.length,
    totalValue: orderCurrencies.length <= 1 ? salesOrders.reduce((sum, so) => sum + so.net_value, 0) : null,
    currency: orderCurrencies.length === 1 ? orderCurrencies[0] : null,
    overdueItems: salesOrders.filter(so => {
      if (!so.delivery_date) return false;
      return new Date(so.delivery_date) < new Date();
    }).length
  };
  
  // Get unique plants for filter
  const uniquePlants = [...new Set(salesOrders.map(so => so.delivery_plant).filter(p => p && p.trim() !== ''))];
  
  // Get unique customers for grouping
  const uniqueCustomers = [...new Set(salesOrders.map(so => so.customer_name))];
  
  const formatCurrency = (value, currency = "USD") => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };
  
  const getStatusBadge = (status, deliveryDate) => {
    if (isOverdue(deliveryDate)) {
      return <Badge variant="destructive">Overdue</Badge>;
    }
    switch (status.toLowerCase()) {
      case "open":
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Open</Badge>;
      case "partial":
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Partial</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };
  
  return (
    <div data-testid="sales-orders-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title heading-display" data-testid="page-title">Open Sales Orders</h1>
          <p className="text-muted-foreground mt-1 flex items-center gap-2">
            {dataSource === "sap_live" ? (
              <><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />Live data from SAP S/4HANA</>
            ) : dataSource === "loading" ? (
              <><span className="w-2 h-2 rounded-full bg-blue-500" />Loading SAP data...</>
            ) : (
              <><span className="w-2 h-2 rounded-full bg-red-500" />{dataSource === "NO_SAP_CONNECTION" ? "No SAP connection" : dataSource === "SAP_UNREACHABLE" ? "SAP unreachable" : dataSource === "SAP_UNAUTHORIZED" ? "SAP credentials rejected" : dataSource === "SAP_FORBIDDEN" ? "SAP authorization required" : dataSource === "SAP_SERVICE_NOT_FOUND" ? "SAP service not activated or path incorrect" : dataSource === "SAP_INVALID_QUERY" ? "SAP query or mapping error" : "SAP data unavailable"}</>
            )}
          </p>
        </div>
        <Button 
          onClick={handleRefresh} 
          variant="outline" 
          className="btn-industrial"
          disabled={refreshing}
          data-testid="refresh-button"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="stat-card" data-testid="stat-total-orders">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="stat-card-value">{stats.totalOrders}</p>
              <p className="stat-card-label">Sales Orders</p>
            </div>
          </div>
        </div>
        <div className="stat-card" data-testid="stat-total-items">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-blue-500/10 flex items-center justify-center">
              <Truck className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="stat-card-value">{stats.totalItems}</p>
              <p className="stat-card-label">Open Items</p>
            </div>
          </div>
        </div>
        <div className="stat-card" data-testid="stat-total-value">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-emerald-500/10 flex items-center justify-center">
              <span className="text-emerald-600 font-bold text-lg">$</span>
            </div>
            <div>
              <p className="stat-card-value">{stats.totalValue === null ? "Multiple currencies" : formatCurrency(stats.totalValue, stats.currency || "USD")}</p>
              <p className="stat-card-label">Total Value</p>
            </div>
          </div>
        </div>
        <div className="stat-card" data-testid="stat-overdue">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-red-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="stat-card-value">{stats.overdueItems}</p>
              <p className="stat-card-label">Overdue Items</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Filter Bar */}
      <div className="filter-bar" data-testid="filter-bar">
        <div className="search-input-wrapper flex-1 min-w-[200px] max-w-md">
          <Search />
          <Input
            placeholder="Search orders, materials, customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]" data-testid="filter-status">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={plantFilter} onValueChange={setPlantFilter}>
          <SelectTrigger className="w-[140px]" data-testid="filter-plant">
            <Factory className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Plant" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plants</SelectItem>
            {uniquePlants.map(p => (
              <SelectItem key={p} value={p}>Plant {p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {hasActiveFilters && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleClearFilters}
            data-testid="clear-filters"
          >
            <X className="w-4 h-4 mr-1" />
            Clear
          </Button>
        )}
        
        {hasActiveFilters && (
          <Badge variant="secondary" className="ml-auto">
            {salesOrders.length} results
          </Badge>
        )}
      </div>
      
      {/* Sales Orders Table */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-md" />
          ))}
        </div>
      ) : salesOrders.length === 0 ? (
        <div className="empty-state" data-testid="empty-state">
          <ShoppingCart className="empty-state-icon" />
          <h3 className="text-lg font-medium mb-2">No Open Sales Orders</h3>
          <p className="text-muted-foreground mb-4">
            {hasActiveFilters 
              ? "Try adjusting your filters"
              : "No open sales orders found in the system"
            }
          </p>
          {hasActiveFilters && (
            <Button variant="outline" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          )}
        </div>
      ) : (
        <Card className="card-industrial" data-testid="sales-orders-table">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="font-semibold">Sales Order</TableHead>
                  <TableHead className="font-semibold">Customer</TableHead>
                  <TableHead className="font-semibold">Material</TableHead>
                  <TableHead className="font-semibold text-right">Open Qty</TableHead>
                  <TableHead className="font-semibold">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Delivery Date
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold">
                    <div className="flex items-center gap-1">
                      <Factory className="w-4 h-4" />
                      Plant
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Committed
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Committed date from Production Order</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableHead>
                  <TableHead className="font-semibold text-right">Value</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {salesOrders.map((order, idx) => (
                  <TableRow 
                    key={`${order.sales_order}-${order.sales_order_item}-${idx}`}
                    className="hover:bg-secondary/30 transition-colors"
                    data-testid={`sales-order-row-${order.sales_order}-${order.sales_order_item}`}
                  >
                    <TableCell>
                      <div>
                        <span className="data-value font-medium">{order.sales_order}</span>
                        <span className="text-muted-foreground text-xs ml-1">/ {order.sales_order_item}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{order.customer_name}</p>
                        <p className="text-xs text-muted-foreground data-value">{order.customer_number}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm line-clamp-1">{order.material_description}</p>
                        <p className="text-xs text-muted-foreground data-value">{order.material}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="data-value font-medium">{order.open_quantity.toLocaleString()}</span>
                      <span className="text-muted-foreground text-xs ml-1">{order.unit}</span>
                    </TableCell>
                    <TableCell>
                      <span className={`data-value ${isOverdue(order.delivery_date) ? 'text-red-600 font-medium' : ''}`}>
                        {formatDate(order.delivery_date)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="data-value">
                        {order.delivery_plant || "-"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {order.committed_date ? (
                        <span className="data-value text-emerald-600">{formatDate(order.committed_date)}</span>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="data-value">{formatCurrency(order.net_value, order.currency)}</span>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(order.order_status, order.delivery_date)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SalesOrders;
